// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState } from 'react';
import { X, ExternalLink, Loader } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * Legal Page Modal
 * Opens Privacy, Terms, Cookies, and Contact pages in modal with iframe
 */
const LegalPageModal = ({ page, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });

  const pageConfig = {
    privacy: {
      title: 'Privacy Policy',
      url: `${window.location.origin}/privacy`,
      icon: '🔒'
    },
    terms: {
      title: 'Terms & Conditions',
      url: `${window.location.origin}/terms`,
      icon: '📄'
    },
    cookies: {
      title: 'Cookie Policy',
      url: `${window.location.origin}/cookies`,
      icon: '🍪'
    },
    contact: {
      title: 'Contact Us',
      url: `${window.location.origin}/contact`,
      icon: '📞'
    }
  };

  const config = pageConfig[page] || pageConfig.privacy;

  const handleOpenInBrowser = () => {
    window.open(config.url, '_blank', 'noopener,noreferrer');
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title">
      <div ref={trapRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.icon}</span>
            <h2 id="legal-modal-title" className="text-xl font-bold text-gray-900">{config.title}</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenInBrowser}
              className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Browser
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative bg-white overflow-hidden">
          {/* Loading Spinner */}
          {loading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="text-center">
                <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-600">Loading {config.title}...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white p-6">
              <div className="text-center max-w-md">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Page</h3>
                <p className="text-gray-600 mb-6">
                  We couldn't load this page in the app. Please open it in your browser instead.
                </p>
                <button
                  onClick={handleOpenInBrowser}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in Browser
                </button>
              </div>
            </div>
          )}

          {/* Iframe */}
          {!error && (
            <iframe
              src={config.url}
              title={config.title}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-center">
          <p className="text-sm text-gray-600">
            <a href="mailto:support@pricepalcompare.com" className="text-blue-600 hover:text-blue-700 mx-2">
              📧 support@pricepalcompare.com
            </a> | 
            <a href="https://wa.me/447377803015" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 ml-2">
              💬 WhatsApp: +44 7377 803 015
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPageModal;
